/**
 * Phase Gates API client — DEC-15 Option 2 (interim).
 *
 * Live contract only:
 * - Presence/status: GET /work/projects/:id/plan → phases[].gate
 *   (definitionExists, submissionStatus, submissionId)
 * - Actions: POST /work/gate-submissions (open draft)
 *            POST/GET …/:id/submit|approve|reject|approval-state|evidence|readiness
 *            POST …/:id/cancel (withdraw a draft; live since #1777)
 *
 * Dead (do not call from UI — phase-scoped routes 404 on staging):
 * - GET/PUT …/phases/:phaseId/gate
 * - POST …/phases/:phaseId/gate/submissions/draft
 * - GET …/gate-submissions?phaseId=
 */
import { request } from '@/lib/api';

// ─── Types ──────────────────────────────────────────────────

export type GateSubmissionStatus =
  | 'DRAFT'
  | 'SUBMITTED'
  | 'APPROVED'
  | 'REJECTED'
  | 'CANCELLED'
  /** MORE-INFORMATION-1 / G-R11 — attempt closed with a question, not a rejection. */
  | 'MORE_INFORMATION';

export interface GateSubmission {
  id: string;
  organizationId: string;
  workspaceId: string;
  projectId: string;
  phaseId: string;
  gateDefinitionId: string;
  status: GateSubmissionStatus;
  submittedByUserId?: string | null;
  submittedAt?: string | null;
  decisionByUserId?: string | null;
  decidedAt?: string | null;
  decisionNote?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface GateSubmissionEvidence {
  id: string;
  organizationId: string;
  submissionId: string;
  artifactItemId: string;
  attachedByUserId: string;
  createdAt: string;
  /** GATE-EVIDENCE-ATTACH-1: names from the server; null when a row cannot be named. */
  itemName?: string | null;
  artifactId?: string | null;
  artifactName?: string | null;
  artifactType?: string | null;
  itemDeleted?: boolean | null;
  /** EVIDENCE-READ-CONTENT-1: only the item's link and note; the server re-validates the link. */
  content?: { url: string | null; note: string | null } | null;
}

/** One project item that could be attached as evidence (server-paged, with a total). */
export interface GateEvidenceCandidate {
  id: string;
  name: string;
  artifactId: string;
  artifactName: string;
  artifactType: string;
  alreadyAttached: boolean;
}

export interface GateEvidenceCandidatePage {
  items: GateEvidenceCandidate[];
  total: number;
  page: number;
  limit: number;
}

export type ApprovalType = 'ANY_ONE' | 'ALL';
export type ApprovalDecision =
  | 'APPROVED'
  | 'REJECTED'
  | 'ABSTAINED'
  | 'MORE_INFORMATION';
export type StepStatus = 'PENDING' | 'ACTIVE' | 'APPROVED' | 'REJECTED' | 'SKIPPED';
export type ChainStatus = 'NOT_STARTED' | 'IN_PROGRESS' | 'COMPLETED' | 'REJECTED';

export interface StepDecision {
  userId: string;
  decision: ApprovalDecision;
  note: string | null;
  decidedAt: string;
  /**
   * SOD-RENDER-1: true when the decider IS the submitter. Computed
   * authoritatively server-side (DTO-GAPS-1 / #475) — never derive this
   * client-side from comparing userId to the submitter.
   */
  selfApproved?: boolean;
}

export interface StepApprovalState {
  stepId: string;
  stepOrder: number;
  name: string;
  approvalType: ApprovalType;
  minApprovals: number;
  /**
   * D-211 / GAP-2 B: people who can decide this step, and approvals it still
   * needs. `null` or absent = not evaluable (legacy request); never render
   * either as 0.
   */
  possibleApprovers?: number | null;
  awaitingApprovals?: number | null;
  /**
   * #1577 ruling: on an ALL step, required approvers who have not approved and
   * can no longer decide. Above 0 the step can never complete and the server
   * sends no awaiting count. `null` or absent = not evaluable.
   */
  unavailableRequiredApprovers?: number | null;
  status: StepStatus;
  decisions: StepDecision[];
}

export interface ChainExecutionState {
  chainId: string;
  submissionId: string;
  chainStatus: ChainStatus;
  activeStepId: string | null;
  steps: StepApprovalState[];
  /**
   * GATE-API-2 / SOD-RENDER-1: caller-scoped eligibility for Approve.
   * If absent, UI must keep Approve disabled (no client SoD shim).
   */
  callerCanApprove?: boolean;
  callerCannotApproveReason?: string | null;
}

export interface GateEvaluationItem {
  code?: string;
  status?: string;
  message?: string;
  blocking?: boolean;
}

export interface GateEvaluationResult {
  canApprove: boolean;
  items: GateEvaluationItem[];
}

// ─── Helper ─────────────────────────────────────────────────

function unwrap<T>(resp: unknown): T {
  if (resp && typeof resp === 'object' && 'data' in resp) {
    return (resp as { data: T }).data;
  }
  return resp as T;
}

// ─── Live submission-ID API ─────────────────────────────────

/** GATE-SUB-1: open or reuse DRAFT for a phase (idempotent). */
export async function openGateSubmission(input: {
  projectId: string;
  phaseId: string;
}): Promise<GateSubmission> {
  const resp = await request.post<unknown>(`/work/gate-submissions`, {
    projectId: input.projectId,
    phaseId: input.phaseId,
  });
  return unwrap<GateSubmission>(resp);
}

/**
 * DRAFT → SUBMITTED.
 * `response` is required only when the previous attempt ended MORE_INFORMATION
 * (#1114). Omitted otherwise. The body is the request, not a wrapper.
 */
export async function submitGateSubmission(
  submissionId: string,
  body?: { response?: string },
): Promise<GateSubmission> {
  const resp = await request.post<unknown>(
    `/work/gate-submissions/${submissionId}/submit`,
    body?.response !== undefined ? { response: body.response } : {},
  );
  return unwrap<GateSubmission>(resp);
}

export async function getApprovalState(
  submissionId: string,
): Promise<ChainExecutionState | null> {
  const resp = await request.get<unknown>(
    `/work/gate-submissions/${submissionId}/approval-state`,
  );
  return unwrap<ChainExecutionState | null>(resp);
}

/**
 * PHASEGATEPANEL-REJECTION-1 / #1017: #1090 emits denials as HTTP 403 with
 * `{ code: 'AUTH_FORBIDDEN', message }` and no `data`. The shared client
 * throws that through `normalizeAxiosError`. Match status AND the named code —
 * a bare 403 or a 500 would mean something else.
 */
export function isAuthForbiddenDenial(err: unknown): boolean {
  if (!err || typeof err !== 'object') return false;
  const e = err as { status?: unknown; code?: unknown };
  return e.status === 403 && e.code === 'AUTH_FORBIDDEN';
}

/**
 * D6-REVIEW-REACH-1 — the server-resolved review payload.
 *
 * Sends NO workspace id. The server resolves submission -> project -> workspace
 * from the persisted row and stamps the context itself, which is what lets a
 * named approver with no workspace membership reach the gate at all.
 * `lib/api.ts` exempts exactly this path from the WORKSPACE_REQUIRED fast-fail.
 *
 * ⚠️ `canDecide` is the server's answer and the ONLY thing the page may bind
 * its Approve/Reject affordance to. A 200 does NOT mean "may act": the two
 * view-only refusals (ALREADY_DECIDED, NOT_ACTIVE_STEP) also return 200, with
 * canDecide:false. Every other refusal — including reason tokens this client
 * does not recognise — is a 403 and never reaches here.
 */
export interface GateReviewEvidence {
  id: string;
  artifactItemId: string;
  name: string | null;
  attachedByUserId: string;
  createdAt: string;
  /**
   * GATE-REVIEW-VISIBILITY-1. True when this reader may not see the artifact.
   * The page shows Restricted and does not render `name`. A missing name is
   * the same label: the name was withheld.
   */
  restricted?: boolean;
}

/** The caller's OWN decision. Never another approver's, never a history. */
export interface GateReviewOwnDecision {
  decision: ApprovalDecision;
  note: string | null;
  decidedAt: string;
  stepId: string;
  stepName: string | null;
}

/**
 * One recorded decision on this submission (#1043 / GATE-TIMELINE-RENDER-1).
 * Flat record — ruling A3 forbids deriving completeness or verdict from it.
 * `attestations` is the exact text the approver ticked, frozen at insert.
 */
export interface GateReviewTimelineEntry {
  id: string;
  stepId: string;
  stepName: string | null;
  stepOrder: number | null;
  decidedByUserId: string;
  decision: ApprovalDecision;
  note: string | null;
  attestations: string[];
  decidedAt: string;
  /**
   * MORE-INFO-RESPONSE-1 (#1114). Present on MORE_INFORMATION entries.
   * The submitter's answer. null means none was recorded — render nothing.
   */
  response?: string | null;
}

export type ResolvedNotDoneKind = 'CANCELLED' | 'NOT_APPLICABLE' | 'DEFERRED';

export interface ResolvedNotDoneTask {
  taskId: string;
  title: string;
  closureKind: ResolvedNotDoneKind;
  reason: string;
}

export interface GateReviewPriorAttempt {
  submissionId: string;
  attemptNumber: number;
  outcome: string;
  decidedBy: string | null;
  decidedAt: string | null;
}

export interface GateReview {
  submissionId: string;
  status: GateSubmissionStatus;
  projectId: string;
  projectName: string | null;
  /** Server-resolved. The client never supplied this. */
  workspaceId: string;
  /** Submitter of this attempt. Compare to the signed-in user. */
  submittedByUserId?: string | null;
  gateName: string | null;
  /**
   * LIVE definition checklist (#1099 retains this temporarily). Not what the
   * submission was frozen against — do not tick or attest from this field.
   */
  criteria: string[];
  /**
   * REVIEW-PAYLOAD-SNAPSHOT-1 / #1099 — frozen at submit for THIS attempt.
   * ⚠️ `null` and `[]` are different facts: null = no snapshot recorded;
   * `[]` = an empty checklist was recorded. Never collapse with `?? []` for
   * rendering decisions.
   */
  criteriaSnapshot: string[] | null;
  /** A separate list. No claimed mapping to criteria / snapshot. */
  evidence: GateReviewEvidence[];
  /**
   * GATE-READINESS-FE-1. null means the payload did not record closed-not-done
   * tasks. An empty list is not rendered as a blank section.
   */
  resolvedNotDone: Array<{
    taskId: string;
    title: string;
    closureKind: 'CANCELLED' | 'NOT_APPLICABLE' | 'DEFERRED';
    reason: string;
  }> | null;
  canDecide: boolean;
  cannotDecideReason: string | null;
  yourDecision: GateReviewOwnDecision | null;
  /**
   * Every decision recorded on this submission, in chain step order.
   * Present once #1043 ships; treat missing as [] for older envelopes.
   */
  timeline: GateReviewTimelineEntry[];
  /**
   * ATTEMPT-HISTORY-1. Prior submissions of this same gate. Absent until the
   * review payload includes `priorAttempts` (Backend Agent 1).
   */
  priorAttempts?: GateReviewPriorAttempt[] | null;
}

export async function getGateReview(submissionId: string): Promise<GateReview> {
  const resp = await request.get<unknown>(
    `/work/gate-submissions/${submissionId}/review`,
  );
  return unwrap<GateReview>(resp);
}

/**
 * GATE-DECISION-FORM-1 — body always carries `attestations` (exact ticked
 * snapshot text, or `[]`). Optional note unchanged. Panel callers omit the
 * third arg and send an empty manifest.
 */
export async function approveGateSubmission(
  submissionId: string,
  note?: string,
  attestations: string[] = [],
): Promise<ChainExecutionState> {
  const body: { note?: string; attestations: string[] } = { attestations };
  if (note) body.note = note;
  const resp = await request.post<unknown>(
    `/work/gate-submissions/${submissionId}/approve`,
    body,
  );
  return unwrap<ChainExecutionState>(resp);
}

export async function rejectGateSubmission(
  submissionId: string,
  note?: string,
  attestations: string[] = [],
): Promise<ChainExecutionState> {
  const body: { note?: string; attestations: string[] } = { attestations };
  if (note) body.note = note;
  const resp = await request.post<unknown>(
    `/work/gate-submissions/${submissionId}/reject`,
    body,
  );
  return unwrap<ChainExecutionState>(resp);
}

/**
 * MORE-INFORMATION-1 / G-R11 — ask a question instead of deciding.
 * Backend: POST …/request-information with required `note` (DTO + service +
 * DB refuse whitespace-only). Error code: GATE_MORE_INFORMATION_QUESTION_REQUIRED.
 * GATE-DECISION-FORM-1 extends the same body with `attestations`.
 */
export async function requestMoreInformation(
  submissionId: string,
  note: string,
  attestations: string[] = [],
): Promise<ChainExecutionState> {
  const resp = await request.post<unknown>(
    `/work/gate-submissions/${submissionId}/request-information`,
    { note, attestations },
  );
  return unwrap<ChainExecutionState>(resp);
}

/** Match backend blank: no non-whitespace character (`@Matches(/\S/)`). */
export function isBlankMoreInformationQuestion(note: string): boolean {
  return !/\S/.test(note);
}

export const GATE_MORE_INFORMATION_QUESTION_REQUIRED =
  'GATE_MORE_INFORMATION_QUESTION_REQUIRED';

/** Latest MORE_INFORMATION question note on a chain state, if any. */
export function moreInformationQuestionFromState(
  state: ChainExecutionState | null | undefined,
): string | null {
  if (!state) return null;
  for (const step of state.steps) {
    for (const d of step.decisions) {
      if (d.decision === 'MORE_INFORMATION' && d.note && /\S/.test(d.note)) {
        return d.note;
      }
    }
  }
  return null;
}

export async function evaluateGateSubmission(
  submissionId: string,
): Promise<GateEvaluationResult> {
  const resp = await request.get<unknown>(
    `/work/gate-submissions/${submissionId}/evaluate`,
  );
  return unwrap<GateEvaluationResult>(resp);
}

/**
 * READINESS-1 (PR #1284). GET /work/gate-submissions/:submissionId/readiness
 * Envelope: `{ data: { submissionId, phaseId, items } }`.
 * `items[].enforcement` is BLOCKING | ADVISORY | NOT_EVALUABLE.
 * Only BLOCKING may disable Submit. OPEN_TASKS is the blocking item.
 */
export type ReadinessEnforcement = 'BLOCKING' | 'ADVISORY' | 'NOT_EVALUABLE';

export type ReadinessState =
  | 'MET'
  | 'MISSING'
  | 'NOT_REQUIRED'
  | 'NOT_EVALUABLE'
  | 'PRESENT'
  | 'NONE';

export interface ReadinessEntry {
  taskId?: string;
  riskId?: string;
  evidenceId?: string;
  title?: string;
  name?: string | null;
  link?: string | null;
  severity?: string;
  state?: string;
}

export interface ReadinessItem {
  key: string;
  label: string;
  enforcement: ReadinessEnforcement;
  state: ReadinessState;
  detail: string;
  entries: ReadinessEntry[];
  expected?: number | null;
  attached?: number;
  requiredTags?: string[];
}

export interface GateReadiness {
  submissionId: string;
  phaseId: string;
  items: ReadinessItem[];
}

/**
 * GATE-REVIEW-WS-1: pass the workspace the SERVER resolved for this gate (the
 * review's `workspaceId`) when the caller has it. The route takes scope from
 * the persisted submission; a header, if sent, must equal that workspace or the
 * server answers 404 — so the shell's active workspace (empty for a named
 * approver who is not a member, or a different workspace) must not be used.
 */
export async function getGateReadiness(
  submissionId: string,
  workspaceId?: string | null,
): Promise<GateReadiness> {
  const url = `/work/gate-submissions/${submissionId}/readiness`;
  const resp = workspaceId
    ? await request.get<unknown>(url, { headers: { 'x-workspace-id': workspaceId } })
    : await request.get<unknown>(url);
  return unwrap<GateReadiness>(resp);
}

export interface GateApproverStatus {
  userId: string;
  name: string | null;
  decision: string | null;
  decidedAt: string | null;
  lastRemindedAt: string | null;
  nextReminderAllowedAt: string | null;
}

export interface GateStatusView {
  submissionId: string;
  submissionStatus: string;
  activeStepId: string | null;
  steps: Array<{
    stepId: string;
    name: string;
    order: number;
    status: string;
    approvers: GateApproverStatus[];
  }>;
  nextAutomaticReminder: { stage: string; dueAt: string } | null;
  nextAutomaticReminderReason: string | null;
}

export async function getGateStatus(submissionId: string): Promise<GateStatusView | null> {
  const resp = await request.get<unknown>(`/work/gate-submissions/${submissionId}/status`);
  const body = unwrap<GateStatusView | null>(resp);
  if (!body || !Array.isArray(body.steps)) return null;
  return body;
}

export async function remindGateApprovers(submissionId: string): Promise<void> {
  await request.post(`/work/gate-submissions/${submissionId}/remind`);
}

/** Only a BLOCKING item that is not MET may disable Submit. */
export function readinessBlocksSubmit(
  readiness: GateReadiness | null | undefined,
): boolean {
  const items = readiness?.items;
  if (!Array.isArray(items)) return false;
  return items.some(
    (item) => item.enforcement === 'BLOCKING' && item.state !== 'MET',
  );
}

export async function listGateEvidence(
  submissionId: string,
): Promise<GateSubmissionEvidence[]> {
  const resp = await request.get<unknown>(
    `/work/gate-submissions/${submissionId}/evidence`,
  );
  const data = unwrap<GateSubmissionEvidence[] | { items?: GateSubmissionEvidence[] }>(
    resp,
  );
  if (Array.isArray(data)) return data;
  if (data && Array.isArray(data.items)) return data.items;
  return [];
}

/** GATE-EVIDENCE-ATTACH-1: every undeleted item in the submission's project, paged with a total. */
export async function listGateEvidenceCandidates(
  submissionId: string,
  params: { page: number; limit: number; q?: string },
): Promise<GateEvidenceCandidatePage> {
  const resp = await request.get<unknown>(
    `/work/gate-submissions/${submissionId}/evidence-candidates`,
    { params },
  );
  const data = unwrap<Partial<GateEvidenceCandidatePage> | null>(resp);
  if (!data || !Array.isArray(data.items) || typeof data.total !== 'number') {
    throw new Error('Evidence candidates response was not recognized.');
  }
  return {
    items: data.items,
    total: data.total,
    page: typeof data.page === 'number' ? data.page : params.page,
    limit: typeof data.limit === 'number' ? data.limit : params.limit,
  };
}

export async function attachGateEvidence(
  submissionId: string,
  artifactItemId: string,
): Promise<GateSubmissionEvidence> {
  const resp = await request.post<unknown>(
    `/work/gate-submissions/${submissionId}/evidence`,
    { artifactItemId },
  );
  return unwrap<GateSubmissionEvidence>(resp);
}

export async function removeGateEvidence(
  submissionId: string,
  evidenceId: string,
): Promise<void> {
  await request.delete(
    `/work/gate-submissions/${submissionId}/evidence/${evidenceId}`,
  );
}

/**
 * Withdraw a DRAFT gate request (GATE-DRAFT-WITHDRAW-1, #1777). The server decides who
 * may: the draft's creator while they hold a workspace write role, the project's
 * manager, or a workspace owner. A refusal (403) or a state change (409) comes back
 * with the API's own message; callers show it verbatim.
 */
export async function cancelGateSubmission(submissionId: string): Promise<GateSubmission> {
  const resp = await request.post<unknown>(
    `/work/gate-submissions/${submissionId}/cancel`,
    {},
  );
  return unwrap<GateSubmission>(resp);
}
